import { sendEmailCode } from '@/services/authService';
import { validateEmail } from '@/utils/formValidation';
import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * 验证码发送 Hook
 */
export const useVerificationCode = () => {
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 清理定时器
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 开始倒计时
  const startCountdown = useCallback((seconds = 60) => {
    setCountdown(seconds);
    setIsCodeSent(true);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          timerRef.current = null;
          setIsCodeSent(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // 发送验证码
  const sendCode = useCallback(
    async (email: string): Promise<{ success: boolean; error?: string }> => {
      // 验证邮箱
      if (!email) {
        return { success: false, error: 'Please enter email address' };
      }
      if (!validateEmail(email)) {
        return { success: false, error: 'Please enter a valid email address' };
      }

      try {
        setIsLoading(true);
        await sendEmailCode(email);
        startCountdown(60);
        return { success: true };
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to send verification code';
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [startCountdown],
  );

  // 重置状态
  const reset = useCallback(() => {
    clearTimer();
    setIsCodeSent(false);
    setCountdown(0);
    setIsLoading(false);
  }, [clearTimer]);

  // 组件卸载时清理定时器
  const cleanup = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  return {
    isCodeSent,
    countdown,
    isLoading,
    sendCode,
    reset,
    cleanup,
  };
};
